import { submitOnboardingCompletion } from './onboardingAgent';

const DB_NAME = 'jobseeqr-onboarding';
const DB_VERSION = 1;
const STORE_NAME = 'completionOutbox';
const FALLBACK_KEY = 'jobseeqr:onboarding-completion-outbox';
const inFlight = new Map();
const AUTO_RETRY_DELAYS_MS = [60_000, 5 * 60_000, 30 * 60_000];
const MAX_AUTO_ATTEMPTS = AUTO_RETRY_DELAYS_MS.length;

function completionId(userId) {
  const nonce = (typeof window !== 'undefined' && window.crypto?.randomUUID?.())
    || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${userId}:${nonce}`;
}

function openDatabase() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('userId', 'userId', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Unable to open onboarding outbox'));
  });
}

async function runStore(mode, operation) {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);
    let result;
    try {
      result = operation(store);
    } catch (error) {
      database.close();
      reject(error);
      return;
    }
    transaction.oncomplete = () => {
      database.close();
      resolve(result?.result);
    };
    transaction.onerror = () => {
      database.close();
      reject(transaction.error || new Error('Onboarding outbox transaction failed'));
    };
  });
}

function fallbackRecords() {
  if (typeof localStorage === 'undefined') return [];
  try {
    const value = JSON.parse(localStorage.getItem(FALLBACK_KEY) || '[]');
    return Array.isArray(value) ? value : [];
  } catch (_error) {
    return [];
  }
}

function writeFallback(records) {
  if (typeof localStorage === 'undefined') return false;
  try {
    localStorage.setItem(FALLBACK_KEY, JSON.stringify(records));
    return true;
  } catch (_error) {
    return false;
  }
}

async function putRecord(record) {
  try {
    await runStore('readwrite', (store) => store.put(record));
    writeFallback(fallbackRecords().filter((item) => item.id !== record.id));
  } catch (error) {
    const records = fallbackRecords().filter((item) => item.id !== record.id);
    if (!writeFallback([...records, record])) throw error;
  }
  return record;
}

async function getRecord(id) {
  try {
    return await runStore('readonly', (store) => store.get(id))
      || fallbackRecords().find((item) => item.id === id)
      || null;
  } catch (_error) {
    return fallbackRecords().find((item) => item.id === id) || null;
  }
}

async function recordsForUser(userId) {
  try {
    const records = await runStore('readonly', (store) => store.index('userId').getAll(userId));
    const byId = new Map((records || []).map((item) => [item.id, item]));
    fallbackRecords()
      .filter((item) => item.userId === userId)
      .forEach((item) => byId.set(item.id, item));
    return [...byId.values()];
  } catch (_error) {
    return fallbackRecords().filter((item) => item.userId === userId);
  }
}

async function deleteRecord(id) {
  try {
    await runStore('readwrite', (store) => store.delete(id));
  } catch (_error) { /* Fall through and clear the fallback copy. */ }
  writeFallback(fallbackRecords().filter((item) => item.id !== id));
}

export async function queueOnboardingCompletion({ userId, conversation, question, answer }) {
  const existing = (await recordsForUser(userId))
    .sort((left, right) => Number(right.createdAt || 0) - Number(left.createdAt || 0))[0];
  if (existing) return existing.id;
  const record = {
    id: completionId(userId),
    userId,
    conversation,
    question,
    answer,
    deliveryVersion: 2,
    status: 'pending_receipt',
    attempts: 0,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    lastError: '',
    nextAttemptAt: 0,
  };
  await putRecord(record);
  return record.id;
}

export async function hasPendingOnboardingCompletion(userId) {
  return (await recordsForUser(userId)).length > 0;
}

async function processRecord(user, id) {
  const record = await getRecord(id);
  if (!record || record.userId !== user.uid) return null;

  try {
    const accepted = await submitOnboardingCompletion({
      user,
      completionId: record.id,
      conversation: record.conversation,
      question: record.question,
      answer: record.answer,
    });
    await deleteRecord(id);
    return { id, queued: true, accepted };
  } catch (error) {
    const responseStatus = Number(error?.status || 0);
    const canRetryAutomatically = (
      !responseStatus
      || responseStatus >= 500
      || [408, 409, 425, 429].includes(responseStatus)
    );
    const attempts = canRetryAutomatically
      ? Number(record.attempts || 0) + 1
      : MAX_AUTO_ATTEMPTS;
    await putRecord({
      ...record,
      status: canRetryAutomatically ? 'retrying' : 'failed',
      deliveryVersion: 2,
      attempts,
      updatedAt: Date.now(),
      lastError: error?.message || 'Onboarding completion failed',
      nextAttemptAt: canRetryAutomatically
        ? Date.now() + AUTO_RETRY_DELAYS_MS[
          Math.min(attempts - 1, AUTO_RETRY_DELAYS_MS.length - 1)
        ]
        : 0,
    });
    throw error;
  }
}

export function processOnboardingCompletion(user, id) {
  if (inFlight.has(id)) return inFlight.get(id);
  const request = processRecord(user, id).finally(() => inFlight.delete(id));
  inFlight.set(id, request);
  return request;
}

export async function processPendingOnboardingCompletions(user) {
  const records = (await recordsForUser(user.uid))
    .filter((record) => (
      record.deliveryVersion !== 2
      || (
        record.status !== 'failed'
        && Number(record.attempts || 0) < MAX_AUTO_ATTEMPTS
        && Number(record.nextAttemptAt || 0) <= Date.now()
      )
    ))
    .sort((left, right) => Number(left.createdAt || 0) - Number(right.createdAt || 0));
  const completed = [];
  const failed = [];
  for (const record of records) {
    try {
      const result = await processOnboardingCompletion(user, record.id);
      if (result) completed.push(result);
    } catch (error) {
      failed.push({ id: record.id, error });
    }
  }
  return { completed, failed };
}
